import at from 'lodash/at';

const isNullOrUndefined = (value) => value === undefined || value === null;

// sort comparator function that takes values that could be null
// treat a null value as < a defined value
const sortNullableValues = (value1, value2) => {
  // return 0 if both the same
  if (value1 === value2) return 0;

  // treat undefined values as less than defined values
  if (isNullOrUndefined(value1)) { return -1; }
  if (isNullOrUndefined(value2)) { return 1; }

  // compare values as normal
  if (value1 < value2) {
    return -1;
  }
  // value1 and value2 are both defined and value1 > value2
  return 1;
};

// A sort comparator function to sort values with parameters for the sort direction
// and a policy on how to treat nulls.
const sortWithDirection = ({
  value1,
  value2,
  sortDescending, // if true sort in reverse natural order
  nullsLast, // if true, nulls should always come last irrespective of sort order
}) => {
  const ascendingComparison = sortNullableValues(value1, value2);
  const descendingComparison = ascendingComparison * -1;
  // if nulls should always be last, check if one of the values is null
  if (nullsLast) {
    if (isNullOrUndefined(value1) || isNullOrUndefined(value2)) {
      // sortNullableValues treats null value < defined value.
      // We want nulls to always come later in the list, so we should return
      // the descending comparison, where null > defined value.
      return descendingComparison;
    }
  }
  // invert the comparison value if sort is descending
  const comparison = sortDescending ? descendingComparison : ascendingComparison;
  return comparison;
};

// full names should be sorted by the last full word of the name
// E.G 'Daniel van Berzon' should be sorted on 'Berzon'
const getSortValueForFullName = (fullName) => {
  if (!fullName) { return null; }

  // Split the name into words
  const words = fullName
    .toLowerCase()
    .split(/[ ,.]/); // regex for ` ` or `,` or `.`

  // remove last word from array
  const lastWord = words.pop();
  // return a string with the last word first
  return [lastWord, ...words].join();
};

const getArraySortedByProperty = (array, propKey) => {
  // make a copy of the array
  const toSort = [...array];
  return toSort.sort((elem1, elem2) => {
    const value1 = at(elem1, [propKey]);
    const value2 = at(elem2, [propKey]);
    return sortNullableValues(value1, value2);
  });
};

const getArraySortedWithDirectionByProperty = ({
  array,
  propKey,
  sortDescending,
  nullsLast,
}) => {
  // make a copy of the array
  const toSort = [...array];
  return toSort.sort((elem1, elem2) => {
    const value1 = elem1[propKey];
    const value2 = elem2[propKey];
    return sortWithDirection({
      value1,
      value2,
      sortDescending,
      nullsLast,
    });
  });
};

export {
  sortNullableValues,
  sortWithDirection,
  getSortValueForFullName,
  getArraySortedByProperty,
  getArraySortedWithDirectionByProperty,
};

